// Login.js
import React, { useState, useEffect } from "react";
import api from './config/api';
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./config/firebaseConfig";
import { SESSION_EXPIRED_KEY } from "./config/axiosInterceptors";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Backdrop,
  CircularProgress,
  Paper,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import useStore from './config/store';
import avatarImage from './assets/avatar.png';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // Estados para recuperación de contraseña
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Detectar idioma del navegador y configurar i18n
  useEffect(() => {
    const detectAndSetLanguage = () => {
      // 1. Prioridad: idioma del navegador
      const browserLang = navigator.language || navigator.languages?.[0];
      if (browserLang) {
        const langCode = browserLang.split('-')[0];
        if (langCode !== i18n.language) {
          i18n.changeLanguage(langCode);
        }
        return;
      }
      
      // 2. Fallback: español si no se puede detectar
      if (i18n.language !== 'es') {
        i18n.changeLanguage('es');
      }
    };

    detectAndSetLanguage();
  }, [i18n]);

  // Manejar mensajes del state de navegación (ej: desde Register)
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state?.email) {
        setEmail(location.state.email);
      }
    }
  }, [location.state]);

  // Aviso de sesión expirada (lo deja el interceptor global antes del redirect)
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_EXPIRED_KEY)) {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      setError(t('login.sessionExpired'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const { setUser } = useStore.getState();
  
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
  
      const response = await api.get('/users', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      setUser(response.data.response.data);
      navigate('/');
    } catch (err) {
      console.error(err);
      setError(t('login.error.generic'));
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError(null);

    try {
      // Sistema propio: el backend envía el correo con el enlace de reset.
      // La respuesta es siempre genérica (no revela si el email existe).
      await api.post('/users/password-reset/request', {
        email: resetEmail,
        language: i18n.language === 'en' ? 'en' : 'es',
      });
      setResetSuccess(true);
    } catch (err) {
      console.error(err);
      setResetError(t('login.passwordReset.error.generic'));
    } finally {
      setIsResetting(false);
    }
  };

  const openPasswordResetDialog = () => {
    setShowPasswordReset(true);
    setResetEmail(email); // Pre-llenar con el email del formulario de login
    setResetError(null);
    setResetSuccess(false);
  };

  const closePasswordResetDialog = () => {
    setShowPasswordReset(false);
    setResetEmail("");
    setResetError(null);
    setResetSuccess(false);
  };

  const goToRegister = () => {
    navigate('/register');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url("https://cdn.pixabay.com/photo/2018/03/10/09/45/businesswoman-3213659_1280.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={5}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(5px)',
            border: '1px solid rgba(26, 75, 143, 0.2)',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              color: '#1a4b8f',
              fontWeight: 600,
              textAlign: 'center',
              mb: 3
            }}
          >
            {t('login.title')}
          </Typography>

          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ mt: 2, width: "100%" }}
          >
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              required
              label={t('login.form.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#1a4b8f',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#1a4b8f',
                }
              }}
            />
            
            <TextField
              fullWidth
              required
              label={t('login.form.password')}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#1a4b8f',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#1a4b8f',
                }
              }}
            />

            {/* Enlace para recuperar contraseña */}
            <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={openPasswordResetDialog}
                sx={{
                  color: '#111111',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('login.passwordReset.link')}
              </Link>
            </Box>
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading}
              sx={{ 
                mt: 1,
                mb: 2,
                py: 1.5,
                backgroundColor: '#1a4b8f',
                '&:hover': {
                  backgroundColor: '#0d3b7a',
                },
                fontWeight: 'bold',
              }}
            >
              {isLoading ? t('login.form.signingIn') : t('login.form.signIn')}
            </Button>
            
            <Typography variant="body2" align="center" sx={{ mt: 2, mb: 2, color: 'text.secondary' }}>
              {t('login.subtitle')}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Sección de registro */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('login.register.question')}
              </Typography>
              <Button
                variant="outlined"
                onClick={goToRegister}
                sx={{
                  borderColor: '#1a4b8f',
                  color: '#1a4b8f',
                  '&:hover': {
                    borderColor: '#0d3b7a',
                    backgroundColor: 'rgba(26, 75, 143, 0.04)',
                  },
                  fontWeight: 'medium',
                }}
              >
                {t('login.register.button')}
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                {t('login.register.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Dialog para recuperación de contraseña */}
      <Dialog 
        open={showPasswordReset} 
        onClose={closePasswordResetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#111111', fontWeight: 600 }}>
          {t('login.passwordReset.title')}
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('login.passwordReset.subtitle')}
          </Typography>

          {resetSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('login.passwordReset.success')}
            </Alert>
          ) : (
            <Box component="form" onSubmit={handlePasswordReset}>
              {resetError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {resetError}
                </Alert>
              )}

              <TextField
                fullWidth
                required
                label={t('login.passwordReset.form.email')}
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                margin="normal"
                disabled={isResetting}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#1a4b8f',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1a4b8f',
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={closePasswordResetDialog}
            sx={{ color: '#666' }}
          >
            {resetSuccess ? t('login.passwordReset.form.backToLogin') : t('cancel')}
          </Button>
          
          {!resetSuccess && (
            <Button
              onClick={handlePasswordReset}
              variant="contained"
              disabled={isResetting || !resetEmail}
              sx={{
                backgroundColor: '#1a4b8f',
                '&:hover': {
                  backgroundColor: '#0d3b7a',
                },
              }}
            >
              {isResetting ? t('login.passwordReset.form.sending') : t('login.passwordReset.form.sendButton')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Backdrop con la imagen durante la carga */}
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: '#fff',
          flexDirection: 'column',
          // backgroundImage: `url(${avatarImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backgroundBlendMode: 'overlay',
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: '#fff' }}>
          {t('login.loading')}
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default Login;
